import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import md5 from "crypto-js/md5";
import { Input, Button, Card, Form, Alert } from "antd";
import { useLanguage } from "../../contexts/LanguageContext";
import Loader from "./Loader";
import config from "../../config.json"; // adjust path to match where config.json actually lives

// Shared helper - keep this in sync with (or better, import from) the same
// util used by the Login component so registration and login always agree
// on what a "clean" mobile number / username looks like.
const cleanMobile = (value) =>
  value ? value.replace(/[^0-9]/g, "").slice(0, 12) : value;

// Strips anything that isn't a letter (no digits, spaces, or symbols).
// If you need to support hyphenated or multi-word names (e.g. "Anne-Marie",
// "Van Der Berg"), let me know and I'll allow spaces/hyphens too.
const cleanAlpha = (value) => (value ? value.replace(/[^A-Za-z]/g, "") : value);

const RegisterUser = ({hubSettings, theme }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState([]);

  // Fallback: if the parent didn't pass a resolved theme, resolve it here
  // directly from config.json using the current hub's code. This means the
  // label override works for DESTEA (hubCode: "client") even if the parent
  // component hasn't been updated to pass `theme` yet. Every other hub keeps
  // showing the normal default label, since only "client" has
  // mobileFieldLabel set in config.json.
  const resolvedTheme =
    theme ||
    config.hubThemeConfig?.[hubSettings?.hubCode] ||
    config.hubThemeConfig?.default;

  const mobileLabel = resolvedTheme?.mobileFieldLabel || t('auth_mobile_number');

  const handleSubmit = async (values) => {
    setLoading(true);
    setError("");

    const mobile = cleanMobile(values.mobile);

    const requestData = {
    
      url: window.DOMAIN.split("/exist")[0] + "/apicall/sipSignup",
      method: "POST",
      data: JSON.stringify({
        ...values,
        mobile,
        passwordstr: values.password,
        password: md5(values.password).toString(),
        hubCode: hubSettings.hubCode,
      }),
      cacheUUID: "",
    };
    setData({ ...values, mobile });

    try {
      const result = await window.apicall(requestData);
      if (result && result.message === "Complete" && !result.isError) {
        setSuccess(true);
      } else {
        setError("Registration failed. Please try again.");
      }
    } catch (error) {
      if(error.responseJSON && error.responseJSON.isError){
        setError(error.responseJSON.message || "Registration failed. Please try again.");
      }
      console.error("Error registering user:", error);
     
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div style={{background: 'linear-gradient(135deg, rgb(216 227 234) 0%, rgb(198 217 219) 100%)', minHeight: '100vh'}} className="flex p-4">
        <Card className="p-6 w-full max-w-md shadow-lg" style={{ marginTop:'10%', maxWidth: 600, margin: "20px auto", minHeight: '740px' }}>
          {success ? (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-4">Registration Successful</h2>
              <p className="mb-4">{`You have successfully registered with username ${data.mobile}`}</p>
              <p className="mb-4">{`You will get login details on registered email.`}</p>
              <Button className='buttonStyle bg-color' type="primary" onClick={() => navigate("/login")}>
                Go to Login
              </Button>
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-bold text-center mb-4">{t('btn_register')}</h2>
              <p className="mb-4">{hubSettings.registrationText}</p>
              {error && <Alert message={error} type="error" showIcon className="mb-4" />}
              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item name="title" label={t('auth_title')} rules={[{ required: true, message: t('auth_title_required') }]}>
                  <Input placeholder={t('auth_title_placeholder')} />
                </Form.Item>
                <Form.Item name="firstname" label={t('auth_first_name')} rules={[{ required: true, message: t('auth_first_name_required') },
                    {
                      pattern: /^[A-Za-z]+$/,
                      message: "First name must contain letters only",
                    },
                  ]}
                  normalize={cleanAlpha}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="surname" label={t('auth_surname')} rules={[{ required: true, message: t('auth_surname_required') },
                    {
                      pattern: /^[A-Za-z]+$/,
                      message: "Surname must contain letters only",
                    },
                  ]}
                  normalize={cleanAlpha}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="mobile" label={mobileLabel} rules={[{ required: true, message: t('auth_mobile_number_required') },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve(); // required rule handles empty
                        if (!/^[0-9]+$/.test(value)) {
                          return Promise.reject(
                            "Mobile number must contain digits only (no spaces or +)"
                          );
                        }
                        // Local SA format: single leading 0, exactly 10 digits total
                        // e.g. 0712345678
                        const isLocal = /^0[0-9]{9}$/.test(value) && !value.startsWith("00");
                        // International format: 2 or 3 digit country code (optionally
                        // preceded by the "00" dialing prefix), 11-12 digits total
                        // e.g. 27789318700, 004789318700
                        const isInternational = /^[0-9]{11,12}$/.test(value);
                        if (isLocal || isInternational) return Promise.resolve();
                        return Promise.reject(
                          "Enter a valid mobile number: 10 digits starting with 0 (e.g. 0712345678), or with country code (e.g. 27712345678)"
                        );
                      },
                    },
                  ]}
                  normalize={cleanMobile}
                >
                  <Input type="tel" placeholder="e.g. 0712345678" maxLength={12} />
                </Form.Item>
                <Form.Item name="email" label={t('auth_email')} rules={[{ required: true, message: t('auth_email_required') }]}>
                  <Input type="email" />
                </Form.Item>
                <Form.Item name="password" label={t('auth_password')} rules={[{ required: true, message: t('auth_password_required') }]}>
                  <Input.Password />
                </Form.Item>
                <div className="flex justify-between w-full">
                  {hubSettings.showCancelButton?<Button type="link" onClick={() => navigate("/login")} style={{ float: "left" }}>
                    Cancel
                  </Button>:''}
                  <Button type="primary" htmlType="submit" className='buttonStyle bg-color' style={{ float: "right" }} loading={loading}>
                    Register
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

export default RegisterUser;
